import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import './Payment.css';

// PayPal Script Loader Hook
const usePayPalScript = (clientId) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!clientId) return;

    if (window.paypal) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
    script.async = true;
    
    script.onload = () => {
      setIsLoaded(true);
    };
    
    script.onerror = () => {
      console.error('Failed to load PayPal SDK');
      setIsLoaded(false);
    };

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [clientId]);

  return isLoaded;
};

const Payment = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [userEmail, setUserEmail] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [paypalError, setPaypalError] = useState('');
  const [userId, setUserId] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  
  const PAYPAL_CLIENT_ID = 'AccWl2Ww5ynRhxF-UFvDYwQFBWnwQgztp58sD7fuBwUJu3bE-l8-YrSpEBQPqJQqNoRKEQkMSF4M2edZ';
  const isPayPalLoaded = usePayPalScript(PAYPAL_CLIENT_ID);

  useEffect(() => {
    const initializePayment = () => {
      const email = sessionStorage.getItem('userEmail');
      const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
      const plan = location.state?.plan;

      if (!email || !plan) {
        navigate('/pricing');
        return;
      }

      setUserEmail(email);
      setSelectedPlan(plan);
      setUserId(userData._id || '');
    };

    initializePayment();
  }, [location, navigate]);

  // Store payment in database
  const storePaymentInDB = async (paymentData) => {
    try {
      console.log('💰 Storing payment data:', paymentData);
      
      const response = await axios.post(
        `${API_BASE_URL}/api/payments/success`,
        paymentData
      );

      if (response.data.success) {
        console.log('✅ Payment stored successfully in database');
        
        // Store the JWT token and user data if provided
        if (response.data.data.token && response.data.data.user) {
          sessionStorage.setItem('authToken', response.data.data.token);
          sessionStorage.setItem('userData', JSON.stringify(response.data.data.user));
          sessionStorage.setItem('userEmail', response.data.data.user.email);
          
          console.log('🔐 User session created after payment:', {
            token: response.data.data.token ? 'Yes' : 'No',
            userEmail: response.data.data.user.email,
            paymentStatus: response.data.data.user.subscription?.paymentStatus
          });
        }
        
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Payment storage failed' };
    } catch (error) {
      console.error('❌ Error storing payment in database:', error);
      console.error('Error details:', error.response?.data);
      return { success: false, error: error.response?.data?.message || 'Payment storage failed' };
    }
  };

  const handleGoToDashboard = () => {
    console.log('🚀 Navigating to dashboard...');
    
    // Clear any redirect flags
    sessionStorage.removeItem('redirectAfterLogin');
    sessionStorage.removeItem('selectedPlanAfterLogin');
    
    navigate('/hiring-dashboard');
  };

  const handleGoToHiringForm = () => {
    navigate('/hiring-form');
  };

  const handleRetryPayment = () => {
    window.location.reload();
  };

  const createOrder = async () => {
    if (!selectedPlan) return;

    try {
      setProcessing(true);
      setPaypalError('');

      const amountInUSD = (selectedPlan.price / 75).toFixed(2);

      return await window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'paypal'
        },

        createOrder: function(data, actions) {
          return actions.order.create({
            purchase_units: [{
              description: `${selectedPlan.name} Subscription`,
              amount: {
                value: amountInUSD,
                currency_code: 'USD'
              }
            }],
            application_context: {
              shipping_preference: 'NO_SHIPPING'
            }
          });
        },

        onApprove: async function(data, actions) {
          try {
            console.log('🔄 Processing PayPal approval...');
            const order = await actions.order.capture();
            console.log('✅ PayPal order captured:', order);
            
            // Generate receipt data
            const receiptData = {
              invoiceNumber: `INV-${Date.now()}`,
              date: new Date().toISOString(),
              plan: selectedPlan.name,
              amount: selectedPlan.price,
              paymentId: data.orderID,
              subscriptionPeriod: selectedPlan.period,
              paypalOrderId: data.orderID,
              payerEmail: order.payer.email_address,
              payerName: `${order.payer.name.given_name} ${order.payer.name.surname}`,
              paypalPayerId: order.payer.payer_id
            };

            // Store payment in database
            const paymentData = {
              userId: userId || '', 
              userEmail: userEmail,
              planId: selectedPlan.id,
              planName: selectedPlan.name,
              amount: selectedPlan.price,
              paypalOrderId: data.orderID,
              paypalPayerId: order.payer.payer_id,
              payerEmail: order.payer.email_address,
              payerName: `${order.payer.name.given_name} ${order.payer.name.surname}`,
              invoiceNumber: receiptData.invoiceNumber,
              subscriptionPeriod: selectedPlan.period,
              receiptData: receiptData
            };

            console.log('📦 Sending payment data to backend:', paymentData);
            
            const dbResult = await storePaymentInDB(paymentData);

            if (dbResult.success) {
              setReceipt(receiptData);
              setPaymentId(data.orderID);
              setPaymentStatus('success');
              setPaymentSuccess(true);

              console.log('✅ Payment completed successfully, preparing dashboard redirect...');
              
              // Auto-redirect to dashboard after 3 seconds
              setTimeout(() => {
                console.log('🔄 Auto-redirecting to dashboard...');
                handleGoToDashboard();
              }, 3000);

            } else {
              throw new Error(dbResult.error || 'Failed to store payment in database');
            }

          } catch (error) {
            console.error('❌ PayPal capture error:', error);
            setPaypalError('Payment processing failed. Please contact support.');
            setPaymentStatus('failed');
            setProcessing(false);
          }
        },

        onError: function(err) {
          console.error('❌ PayPal error:', err);
          setPaypalError('Payment failed. Please try again.');
          setPaymentStatus('failed');
          setProcessing(false);
        },

        onCancel: function(data) {
          console.log('⚠️ Payment cancelled by user');
          setPaymentStatus('cancelled');
          setProcessing(false);
        }

      }).render('#paypal-button-container');
      
    } catch (error) {
      console.error('❌ PayPal initialization error:', error);
      setPaypalError('Failed to initialize payment. Please try again.');
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (isPayPalLoaded && selectedPlan && !processing) {
      createOrder();
    }
  }, [isPayPalLoaded, selectedPlan]);

  if (!selectedPlan) {
    return (
      <div className="payment-loading">
        <div className="loading-spinner"></div>
        <p>Loading payment details...</p>
      </div>
    );
  }

  return (
    <div className="payment-container">
      <div className="payment-header">
        <h1>Complete Your Payment</h1>
        <p>Pay securely with PayPal</p>
        {userEmail && (
          <p className="user-email-display">Account: <strong>{userEmail}</strong></p>
        )}
      </div>

      <div className="payment-card">
        <div className="plan-summary">
          <h3>{selectedPlan.name}</h3>
          <div className="plan-price">
            ₹{selectedPlan.price} / {selectedPlan.period}
          </div>
          <div className="converted-price">
            ≈ ${(selectedPlan.price / 75).toFixed(2)} USD
          </div>
          {userEmail && (
            <p className="billing-email">Billing Email: {userEmail}</p>
          )}
        </div>

        {paymentStatus === 'pending' && (
          <div className="payment-pending">
            {!isPayPalLoaded ? (
              <div className="paypal-loading">
                <div className="loading-spinner"></div>
                <p>Loading PayPal...</p>
              </div>
            ) : (
              <>
                {paypalError && (
                  <div className="error-message">
                    {paypalError}
                  </div>
                )}
                
                <div className="paypal-button-container">
                  <div id="paypal-button-container"></div>
                </div>

                <div className="payment-security">
                  <div className="security-badges">
                    <div className="security-badge">🔒 Secure</div>
                    <div className="security-badge">🛡️ Protected</div>
                    <div className="security-badge">💳 Encrypted</div>
                  </div>
                  <p className="security-text">
                    Your payment is securely processed by PayPal. We never store your payment details.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {paymentStatus === 'success' && receipt && (
          <div className="payment-success">
            <div className="success-icon">✅</div>
            <h3>Payment Successful!</h3>
            <p>Your {selectedPlan.name} has been activated successfully.</p>
            <p className="email-notification">
              📧 A confirmation email has been sent to <strong>{receipt.payerEmail || userEmail}</strong>
            </p>
            <p className="db-success">✓ Payment details stored in database</p>
            <p className="redirect-notice">
              🔄 Redirecting to dashboard in 3 seconds...
            </p>
            
            <div className="receipt-section">
              <h4>Payment Receipt</h4>
              <div className="receipt-details">
                <div className="receipt-row">
                  <span>Invoice Number:</span>
                  <strong>{receipt.invoiceNumber}</strong>
                </div>
                <div className="receipt-row">
                  <span>Date:</span>
                  <span>{new Date(receipt.date).toLocaleDateString()}</span>
                </div>
                <div className="receipt-row">
                  <span>Plan:</span>
                  <span>{receipt.plan}</span>
                </div>
                <div className="receipt-row">
                  <span>Amount Paid:</span>
                  <strong>₹{receipt.amount}</strong>
                </div>
                <div className="receipt-row">
                  <span>PayPal Order ID:</span>
                  <span className="order-id">{receipt.paypalOrderId}</span>
                </div>
                <div className="receipt-row">
                  <span>Payer Email:</span>
                  <span>{receipt.payerEmail}</span>
                </div>
                <div className="receipt-row">
                  <span>Subscription Period:</span>
                  <span>{receipt.subscriptionPeriod}</span>
                </div>
              </div>
            </div>

            <div className="success-actions">
              <button 
                onClick={handleGoToDashboard}
                className="go-to-dashboard-btn primary"
              >
                🚀 Go to Hiring Dashboard Now
              </button>
              <button 
                onClick={handleGoToHiringForm}
                className="go-to-hiring-form-btn secondary"
              >
                📝 Fill Hiring Form
              </button>
            </div>
          </div>
        )}

        {paymentStatus === 'failed' && (
          <div className="payment-failed">
            <div className="failed-icon">❌</div>
            <h3>Payment Failed</h3>
            <p>{paypalError || 'There was an error processing your payment.'}</p>
            <button 
              onClick={handleRetryPayment}
              className="retry-payment-btn"
            >
              🔄 Try Again
            </button>
          </div>
        )}

        {paymentStatus === 'cancelled' && (
          <div className="payment-cancelled">
            <div className="cancelled-icon">⚠️</div>
            <h3>Payment Cancelled</h3>
            <p>Your payment was cancelled. You can try again anytime.</p>
            <button 
              onClick={handleRetryPayment}
              className="retry-payment-btn"
            >
              🔄 Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payment;